/**
 * DriveZone — Axios API Instance
 *
 * Central HTTP client used by every view and component in the app.
 *
 * Key configuration:
 *   withCredentials: true  — Sends the session cookie on every request.
 *                            Required for Django session authentication.
 *                            Without this, the user appears logged out on every call.
 *
 * Interceptors:
 *   Response interceptor — watches for 401 responses on authenticated endpoints.
 *   Only dispatches session_invalidated when certain the session is truly gone,
 *   not when it may be a transient race condition right after login.
 */

import axios from 'axios'
import { useAuthStore } from '@/stores/authStore'

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL ?? '',
  withCredentials: true,   // CRITICAL — must be true for session cookies to work
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  timeout: 30000, // 30 second timeout
})


// ── Request Interceptor ──────────────────────────────────────────────────
// Add CSRF token to all mutating requests (POST, PUT, PATCH, DELETE).
// Django's CSRF middleware requires this for session-authenticated requests.
api.interceptors.request.use(
  (config) => {
    // Read the CSRF token from the cookie Django sets automatically
    const csrfToken = getCookie('csrftoken')
    if (csrfToken && config.method !== 'get') {
      config.headers['X-CSRFToken'] = csrfToken
    }
    return config
  },
  (error) => Promise.reject(error)
)


// Public auth endpoints legitimately return 401 for wrong credentials/codes
// (invalid_credentials, invalid_otp, token_expired) — those are normal,
// expected failures on a page with no session yet, not a session-invalidation
// event. Excluding them stops the interceptor from firing a global redirect
// while a user is just typing a wrong password or OTP.
const PUBLIC_AUTH_PATHS = [
  '/api/auth/login/',
  '/api/auth/register/',
  '/api/auth/verify-otp/',
  '/api/auth/resend-otp/',
  '/api/auth/forgot-password/',
  '/api/auth/verify-reset-code/',
  '/api/auth/confirm-reset/',
  // AuthGuard calls this on every protected-route mount to check whether a
  // session exists at all. A 401 here for a visitor who was never logged in
  // is normal, silent, and already handled locally by AuthGuard's redirect —
  // it must not trigger a "your session expired" toast.
  '/api/auth/me/',
]

// ── Response Interceptor ─────────────────────────────────────────────────
// Handles global 401 errors.
//
// We only fire the session_invalidated event when we're confident the
// session is TRULY gone, not when it might be a race condition right
// after login.  Two safe triggers:
//
//   1. The server returned error:"session_invalidated" — SingleSessionMiddleware
//      explicitly found an inactive UserSession record (another device logged in).
//
//   2. The user is NOT in the Zustand store — their session genuinely expired
//      (90-day TTL elapsed, Redis evicted the key, etc.).
//
// If the user IS authenticated in Zustand but gets a plain 401 (no specific
// error code), it's almost certainly a race condition right after login where
// the first post-login request hit the server before the session cookie was
// fully committed.  In that case we let the request's own catch block handle
// it silently rather than kicking the user back to /login.
api.interceptors.response.use(
  // Success — pass through unchanged
  (response) => response,

  // Error — inspect and handle
  (error) => {
    const status = error.response?.status
    const errorCode = error.response?.data?.error
    const url = error.config?.url ?? ''
    const isPublicAuthEndpoint = PUBLIC_AUTH_PATHS.some((p) => url.includes(p))

    if (status === 401 && !isPublicAuthEndpoint) {
      const isAuthenticated = useAuthStore.getState().isAuthenticated

      // Only dispatch when we're sure it's a real session loss.
      const isKickedByServer = errorCode === 'session_invalidated'
      const isExpiredSession = !isAuthenticated

      if (isKickedByServer || isExpiredSession) {
        window.dispatchEvent(
          new CustomEvent('drivezone:session_invalidated', {
            detail: {
              message: isKickedByServer
                ? error.response.data.message
                : 'Your session has expired. Please log in again.',
            }
          })
        )
      }
    }

    return Promise.reject(error)
  }
)


// ── Helper: read a cookie by name ────────────────────────────────────────
function getCookie(name: string): string | null {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() ?? null
  }
  return null
}


export default api
